import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LogOut,
  AlertTriangle,
  Building2,
  Briefcase,
  ChevronDown,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { client } from '../api/client';
import type { Company, Page } from '../types';
import { navFor } from '../lib/nav';
import type { NavItem } from '../lib/nav';
import AlertWatcher from './AlertWatcher';
import PingWatcher from './PingWatcher';
import {
  ROLE_LABELS,
  canSeeAllCompanies,
  useAuth,
} from '../store/auth';

/**
 * Query keys whose answer does not depend on which company is selected.
 *
 * These are the screens fetched with `skipActingCompany`: the request carries
 * no company_id, so the server returns the same rows either way. Resetting them
 * on a switch would blank this picker's own dropdown mid-selection and refetch
 * a cross-company dashboard that cannot have changed.
 *
 * Missing an entry here is harmless — the query refetches data it already had.
 * That is the opposite of the risk in the query keys themselves, where an
 * omission shows another company's rows, which is why the switch resets
 * everything by default and this stays a short list of exceptions.
 */
const COMPANY_INDEPENDENT_KEYS = new Set([
  'acting-companies',
  'admin-companies',
  'overview',
]);

/**
 * Which company the owner is acting as.
 *
 * A super admin belongs to no company, so the API cannot infer one and refuses
 * every company-scoped write without an explicit company_id. Choosing here
 * once beats a selector on ten separate forms, and matches how the work
 * actually goes: an owner works inside one company for a stretch rather than
 * switching per record.
 *
 * Nobody else ever sees this — everyone else has exactly one company and the
 * API fills it in for them.
 */
function ActingCompanyPicker() {
  const actingCompanyId = useAuth((state) => state.actingCompanyId);
  const setActingCompany = useAuth((state) => state.setActingCompany);
  const queryClient = useQueryClient();

  /**
   * Switch company, and drop everything cached under the old one.
   *
   * The request interceptor puts the acting company on every call, but React
   * Query caches by key and none of the 62 keys in this app mention it. Without
   * this, switching company re-renders the previous company's rows from cache
   * until something happens to invalidate them — which reads as a refetch bug
   * and looks exactly like a tenancy leak.
   *
   * Reset rather than cleared. `queryClient.clear()` removes queries from the
   * cache but leaves every mounted observer holding the destroyed query and its
   * old data, so nothing refetches and the previous company's rows stay on
   * screen until a manual reload — which is the bug this was meant to fix.
   * resetQueries puts each query back to pending, so the screen shows a loading
   * state rather than another company's records, and then refetches the active
   * ones with the new company applied.
   *
   * Done here rather than in the auth store because the store is imported by
   * the api client; reaching back for the query client would be a cycle — and
   * this is the only place the company changes.
   */
  const switchCompany = (companyId: string | null) => {
    setActingCompany(companyId);
    void queryClient.resetQueries({
      predicate: (query) => !COMPANY_INDEPENDENT_KEYS.has(String(query.queryKey[0])),
    });
  };

  const companies = useQuery<Page<Company>>({
    queryKey: ['acting-companies'],
    queryFn: async () =>
      (
        await client.get('/api/v1/admin/companies', {
          params: { limit: 200 },
          // Listing the companies must not be narrowed to one of them.
          skipActingCompany: true,
        })
      ).data,
  });

  const options = (companies.data?.items ?? []).filter((c) => c.isActive);

  return (
    <label className="flex items-center gap-2">
      <Building2
        className={`h-4 w-4 ${actingCompanyId ? 'text-indigo-600' : 'text-amber-500'}`}
      />
      <span className="sr-only">Acting as company</span>
      <select
        className="input py-1 text-xs"
        value={actingCompanyId ?? ''}
        onChange={(event) => switchCompany(event.target.value || null)}
        title="Which Company You Are Working in"
      >
        {/* Not preselected. Guessing which company an owner meant is how a
            holiday lands in the wrong one. */}
        <option value="">Choose a company…</option>
        {options.map((company) => (
          <option key={company.id} value={company.id}>
            {company.name}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Shown to an owner who has not chosen a company yet.
 *
 * Reading works without a choice — lists simply are not narrowed. Creating
 * does not: the API refuses every company-scoped write with "super admin must
 * specify a company_id", which describes our internals rather than what the
 * person did wrong. Saying so up front beats letting them fill in a form and
 * meet that on submit.
 *
 * Hidden on the two screens that are cross-company by design, where having no
 * company selected is the correct state and a prompt to fix it would be wrong.
 */
function NoCompanyBanner() {
  const { pathname } = useLocation();
  const crossCompany = pathname.startsWith('/admin/overview')
    || pathname.startsWith('/admin/companies');

  if (crossCompany) return null;

  return (
    <div className="border-b border-amber-200 bg-amber-50">
      <div className="mx-auto flex max-w-6xl items-start gap-2 px-4 py-2.5">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <p className="text-xs text-amber-900">
          <strong>No company selected.</strong> You can read across every
          company, but adding anything — a holiday, a call, a job opening, a
          payroll run — needs to know which company it belongs to. Choose one
          from the picker above.
        </p>
      </div>
    </div>
  );
}

/**
 * One group of tabs, as a dropdown.
 *
 * Wrapping instead of scrolling: the old bar put twenty-odd tabs in a
 * horizontal scroller, so on a laptop the last third of them existed only if
 * you knew to drag sideways.
 *
 * The button reads as active whenever the open route belongs to it, so the
 * current screen is findable without opening anything — a collapsed group
 * that gives no sign it holds the active page is worse than a long bar.
 */
function NavGroupMenu({ label, items }: { label: string; items: NavItem[] }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const container = useRef<HTMLDivElement>(null);

  const holdsCurrentPage = items.some(
    (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
  );

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={container}>
      <button
        type="button"
        className={`flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5
                    text-sm font-medium transition-colors ${
                      holdsCurrentPage
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-900'
                    }`}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((was) => !was)}
      >
        {label}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 z-30 mt-0.5 min-w-48 rounded-lg border border-gray-200
                     bg-white py-1 shadow-lg"
        >
          {items.map(({ to, label: itemLabel, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              role="menuitem"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-indigo-50 font-medium text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {itemLabel}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppLayout() {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const navigate = useNavigate();
  const actingCompanyId = useAuth((state) => state.actingCompanyId);

  const { topLevel, groups } = navFor(user);

  return (
    <div className="flex h-screen min-h-screen min-h-0 flex-col overflow-hidden">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold tracking-tight text-gray-900">
              BNB India
            </span>
          </div>

          <div className="flex items-center gap-3">
            {canSeeAllCompanies(user) && <ActingCompanyPicker />}
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">{user?.fullName}</p>
              <p className="text-xs text-gray-500">
                {user ? ROLE_LABELS[user.role] : ''}
              </p>
            </div>
            <button
              type="button"
              className="btn-ghost"
              onClick={async () => {
                await logout();
                navigate('/login', { replace: true });
              }}
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>

        <nav className="mx-auto flex max-w-6xl flex-wrap items-center gap-1 px-4">
          {topLevel.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm
                 font-medium transition-colors ${
                   isActive
                     ? 'border-indigo-600 text-indigo-600'
                     : 'border-transparent text-gray-500 hover:text-gray-900'
                 }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}

          {groups.length > 0 && (
            <span className="mx-1 h-5 w-px bg-gray-200" aria-hidden="true" />
          )}

          {groups.map((group) =>
            group.asTab ? (
              <NavLink
                key={group.label}
                to={group.items[0].to}
                className={({ isActive }) =>
                  `flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm
                   font-medium transition-colors ${
                     isActive
                       ? 'border-indigo-600 text-indigo-600'
                       : 'border-transparent text-gray-500 hover:text-gray-900'
                   }`
                }
              >
                <Briefcase className="h-4 w-4" />
                {group.label}
              </NavLink>
            ) : (
              <NavGroupMenu
                key={group.label}
                label={group.label}
                items={group.items}
              />
            ),
          )}
        </nav>
      </header>

      {canSeeAllCompanies(user) && !actingCompanyId && <NoCompanyBanner />}

      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl px-4 py-6">
          <Outlet />
        </div>
      </main>

      {/* Polls for a due check-in and blocks the screen when one fires. */}
      <PingWatcher />
      {/* Mounted after PingWatcher and suppressed while its modal is up:
          the check-in owns the screen when it is showing. */}
      <AlertWatcher />
      <Toaster richColors position="bottom-right" />
    </div>
  );
}
